import React, { useState, useEffect } from "react";
import "../../assets/styles/ops/CollapsibleTable.scss";
import { Tooltip as ReactTooltip } from "react-tooltip";

import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import ListItemText from "@mui/material/ListItemText";
import Skeleton from "@mui/material/Skeleton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

function CollapsibleTable({
  parentToChild,
  onResetTable,
  onSelectedReasonChange,
  onSelectedSubReasonChange,
}: {
  parentToChild: any;
  onResetTable: any;
  onSelectedReasonChange: any;
  onSelectedSubReasonChange: any;
}) {
  const {
    startDate,
    endDate,
    selectedCallCenter,
    selectedRadio,
    isBeingResetTable,
    selectedOption,
    mode,
  } = parentToChild;
  const [tableData, setTableData] = useState<Record<string, any>>({});
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [displayText, setDisplayText] = useState<string>("");

  const [selectedReason, setSelectedReason] = useState<string>("");
  const [selectedSubReason, setSelectedSubReason] = useState<string>("");

  const [openRow, setOpenRow] = useState<any | null>(null);

  const handleReasonSelection = (label: string) => {
    // Reset the sub reason
    setSelectedSubReason("");
    onSelectedSubReasonChange("");

    // Set the new reason type
    setSelectedReason(label);
    onSelectedReasonChange(label);
  };

  const handleSubReasonSelection = (label: string) => {
    setSelectedReason("");
    onSelectedReasonChange("");

    setSelectedSubReason(label);
    onSelectedSubReasonChange(label);
  };

  // Reset all useState variables when reset button is pressed in parent component
  useEffect(() => {
    if (isBeingResetTable === true) {
      setSelectedReason("");
      setSelectedSubReason("");
      setOpenRow("");
      onResetTable();
    }
  }, [isBeingResetTable, onResetTable]);

  useEffect(() => {
    switch (selectedRadio) {
      case "total_call_volume":
        setDisplayText("Total Call Volume");
        break;
      case "average_wait_time":
        setDisplayText("Average Wait Time");
        break;
      case "average_handle_time":
        setDisplayText("Average Handle Time");
        break;
      case "resolution_rate":
        setDisplayText("Resolution Rate");
        break;
      case "average_sentiment":
        setDisplayText("Average Sentiment");
        break;
      case "unresolved_calls":
        setDisplayText("Unresolved Calls");
        break;
      default:
        setDisplayText("Total Call Volume");
    }
  }, [selectedRadio]);

  useEffect(() => {
    let tableData: any;
    setIsLoading(true);

    const formattedStartDate = startDate.toISOString().split("T")[0];
    const formattedEndDate = endDate.toISOString().split("T")[0];

    const fetchData = (url: string) => {
      return fetch(url)
        .then((res) => res.json())
        .catch((error) => {
          console.error(`Error fetching data from ${url}:`, error);
          return null; // Handle errors by returning null or an appropriate value
        });
    };

    let apiUrls;
    switch (selectedRadio) {
      case "total_call_volume":
        apiUrls = [
          `/call_volume_by_call_reason_by_subreason_with_percentage_change?start_date=${formattedStartDate}&end_date=${formattedEndDate}&call_center_id=${selectedCallCenter}&callreason=Transaction%20Related`,
          `/call_volume_by_call_reason_by_subreason_with_percentage_change?start_date=${formattedStartDate}&end_date=${formattedEndDate}&call_center_id=${selectedCallCenter}&callreason=Online%20and%20Mobile%20Banking`,
          `/call_volume_by_call_reason_by_subreason_with_percentage_change?start_date=${formattedStartDate}&end_date=${formattedEndDate}&call_center_id=${selectedCallCenter}&callreason=Account%20Management`,
          `/call_volume_by_call_reason_by_subreason_with_percentage_change?start_date=${formattedStartDate}&end_date=${formattedEndDate}&call_center_id=${selectedCallCenter}&callreason=Account%20Setup%20or%20Information`,
        ];
        break;
      case "average_wait_time":
        apiUrls = [
          `/average_wait_time_by_call_reason_by_subreason_with_percentage_change?start_date=${formattedStartDate}&end_date=${formattedEndDate}&call_center_id=${selectedCallCenter}&callreason=Transaction%20Related`,
          `/average_wait_time_by_call_reason_by_subreason_with_percentage_change?start_date=${formattedStartDate}&end_date=${formattedEndDate}&call_center_id=${selectedCallCenter}&callreason=Online%20and%20Mobile%20Banking`,
          `/average_wait_time_by_call_reason_by_subreason_with_percentage_change?start_date=${formattedStartDate}&end_date=${formattedEndDate}&call_center_id=${selectedCallCenter}&callreason=Account%20Management`,
          `/average_wait_time_by_call_reason_by_subreason_with_percentage_change?start_date=${formattedStartDate}&end_date=${formattedEndDate}&call_center_id=${selectedCallCenter}&callreason=Account%20Setup%20or%20Information`,
        ];
        break;
      case "average_handle_time":
        apiUrls = [
          `/average_handletime_by_call_reason_by_subreason_with_percentage_change?start_date=${formattedStartDate}&end_date=${formattedEndDate}&call_center_id=${selectedCallCenter}&callreason=Transaction%20Related`,
          `/average_handletime_by_call_reason_by_subreason_with_percentage_change?start_date=${formattedStartDate}&end_date=${formattedEndDate}&call_center_id=${selectedCallCenter}&callreason=Online%20and%20Mobile%20Banking`,
          `/average_handletime_by_call_reason_by_subreason_with_percentage_change?start_date=${formattedStartDate}&end_date=${formattedEndDate}&call_center_id=${selectedCallCenter}&callreason=Account%20Management`,
          `/average_handletime_by_call_reason_by_subreason_with_percentage_change?start_date=${formattedStartDate}&end_date=${formattedEndDate}&call_center_id=${selectedCallCenter}&callreason=Account%20Setup%20or%20Information`,
        ];
        break;
      case "resolution_rate":
        apiUrls = [
          `/resolution_rate_by_call_reason_by_subreason_with_percentage_change?start_date=${formattedStartDate}&end_date=${formattedEndDate}&call_center_id=${selectedCallCenter}&callreason=Transaction%20Related`,
          `/resolution_rate_by_call_reason_by_subreason_with_percentage_change?start_date=${formattedStartDate}&end_date=${formattedEndDate}&call_center_id=${selectedCallCenter}&callreason=Online%20and%20Mobile%20Banking`,
          `/resolution_rate_by_call_reason_by_subreason_with_percentage_change?start_date=${formattedStartDate}&end_date=${formattedEndDate}&call_center_id=${selectedCallCenter}&callreason=Account%20Management`,
          `/resolution_rate_by_call_reason_by_subreason_with_percentage_change?start_date=${formattedStartDate}&end_date=${formattedEndDate}&call_center_id=${selectedCallCenter}&callreason=Account%20Setup%20or%20Information`,
        ];
        break;
      case "average_sentiment":
        apiUrls = [
          `/average_sentiment_by_call_reason_by_subreason_with_percentage_change?start_date=${formattedStartDate}&end_date=${formattedEndDate}&call_center_id=${selectedCallCenter}&callreason=Transaction%20Related`,
          `/average_sentiment_by_call_reason_by_subreason_with_percentage_change?start_date=${formattedStartDate}&end_date=${formattedEndDate}&call_center_id=${selectedCallCenter}&callreason=Online%20and%20Mobile%20Banking`,
          `/average_sentiment_by_call_reason_by_subreason_with_percentage_change?start_date=${formattedStartDate}&end_date=${formattedEndDate}&call_center_id=${selectedCallCenter}&callreason=Account%20Management`,
          `/average_sentiment_by_call_reason_by_subreason_with_percentage_change?start_date=${formattedStartDate}&end_date=${formattedEndDate}&call_center_id=${selectedCallCenter}&callreason=Account%20Setup%20or%20Information`,
        ];
        break;
      case "unresolved_calls":
        apiUrls = [
          `/total_unresolved_by_call_reason_by_subreason_with_percentage_change?start_date=${formattedStartDate}&end_date=${formattedEndDate}&call_center_id=${selectedCallCenter}&callreason=Transaction%20Related`,
          `/total_unresolved_by_call_reason_by_subreason_with_percentage_change?start_date=${formattedStartDate}&end_date=${formattedEndDate}&call_center_id=${selectedCallCenter}&callreason=Online%20and%20Mobile%20Banking`,
          `/total_unresolved_by_call_reason_by_subreason_with_percentage_change?start_date=${formattedStartDate}&end_date=${formattedEndDate}&call_center_id=${selectedCallCenter}&callreason=Account%20Management`,
          `/total_unresolved_by_call_reason_by_subreason_with_percentage_change?start_date=${formattedStartDate}&end_date=${formattedEndDate}&call_center_id=${selectedCallCenter}&callreason=Account%20Setup%20or%20Information`,
        ];
        break;
      default:
        apiUrls = [
          `/call_volume_by_call_reason_by_subreason_with_percentage_change?start_date=${formattedStartDate}&end_date=${formattedEndDate}&call_center_id=${selectedCallCenter}&callreason=Transaction%20Related`,
          `/call_volume_by_call_reason_by_subreason_with_percentage_change?start_date=${formattedStartDate}&end_date=${formattedEndDate}&call_center_id=${selectedCallCenter}&callreason=Online%20and%20Mobile%20Banking`,
          `/call_volume_by_call_reason_by_subreason_with_percentage_change?start_date=${formattedStartDate}&end_date=${formattedEndDate}&call_center_id=${selectedCallCenter}&callreason=Account%20Management`,
          `/call_volume_by_call_reason_by_subreason_with_percentage_change?start_date=${formattedStartDate}&end_date=${formattedEndDate}&call_center_id=${selectedCallCenter}&callreason=Account%20Setup%20or%20Information`,
        ];
    }

    Promise.all(apiUrls.map((url) => fetchData(url)))
      .then((dataArray) => {
        // dataArray will contain the results in the order of apiUrls
        const [
          reasonTransactionData,
          reasonTechnicalData,
          reasonAccountMgmtData,
          reasonAccountSetupData,
        ] = dataArray;

        let callReasonUrl;
        switch (selectedRadio) {
          case "total_call_volume":
            callReasonUrl = `/product_call_volume_by_call_reason?start_date=${formattedStartDate}&end_date=${formattedEndDate}&call_center_id=${selectedCallCenter}`;
            break;
          case "average_wait_time":
            callReasonUrl = `/average_waittime_by_call_reason?start_date=${formattedStartDate}&end_date=${formattedEndDate}&call_center_id=${selectedCallCenter}`;
            break;
          case "average_handle_time":
            callReasonUrl = `/average_handletime_by_call_reason?start_date=${formattedStartDate}&end_date=${formattedEndDate}&call_center_id=${selectedCallCenter}`;
            break;
          case "resolution_rate":
            callReasonUrl = `/resolution_rate_by_call_reason?start_date=${formattedStartDate}&end_date=${formattedEndDate}&call_center_id=${selectedCallCenter}`;
            break;
          case "average_sentiment":
            callReasonUrl = `/average_sentiment_by_call_reason?start_date=${formattedStartDate}&end_date=${formattedEndDate}&call_center_id=${selectedCallCenter}`;
            break;
          case "unresolved_calls":
            callReasonUrl = `/total_unresolved_by_call_reason?start_date=${formattedStartDate}&end_date=${formattedEndDate}&call_center_id=${selectedCallCenter}`;
            break;
          default:
            callReasonUrl = `/product_call_volume_by_call_reason?start_date=${formattedStartDate}&end_date=${formattedEndDate}&call_center_id=${selectedCallCenter}`;
        }

        fetch(callReasonUrl)
          .then((res) => res.text())
          .then((data) => {
            let resultArray;
            switch (selectedRadio) {
              case "total_call_volume":
                resultArray = JSON.parse(data).map(
                  ({
                    call_volume,
                    percent_change,
                    call_reason,
                  }: {
                    call_volume: string;
                    percent_change: string;
                    call_reason: string;
                  }) => [call_reason, percent_change, parseInt(call_volume, 10)]
                );

                tableData = resultArray.map(
                  ([call_reason, percent_change, call_volume]: [
                    string,
                    string,
                    number
                  ]) => {
                    return createData(call_reason, call_volume, percent_change);
                  }
                );

                resultArray.forEach(
                  (
                    [call_reason, call_volume, percent_change]: [
                      string,
                      number,
                      string
                    ],
                    index: number
                  ) => {
                    switch (call_reason) {
                      case "Transaction Related":
                        tableData[index].sub = reasonTransactionData.map(
                          ({
                            call_subreason,
                            call_volume,
                            percent_change,
                          }: {
                            call_subreason: string;
                            call_volume: string;
                            percent_change: string;
                          }) => ({
                            callsubreason: call_subreason,
                            call_volume: parseInt(call_volume, 10),
                            percent_change,
                          })
                        );
                        break;
                      case "Online and Mobile Banking":
                        tableData[index].sub = reasonTechnicalData.map(
                          ({
                            call_subreason,
                            call_volume,
                            percent_change,
                          }: {
                            call_subreason: string;
                            call_volume: string;
                            percent_change: string;
                          }) => ({
                            callsubreason: call_subreason,
                            call_volume: parseInt(call_volume, 10),
                            percent_change,
                          })
                        );
                        break;
                      case "Account Management":
                        tableData[index].sub = reasonAccountMgmtData.map(
                          ({
                            call_subreason,
                            call_volume,
                            percent_change,
                          }: {
                            call_subreason: string;
                            call_volume: string;
                            percent_change: string;
                          }) => ({
                            callsubreason: call_subreason,
                            call_volume: parseInt(call_volume, 10),
                            percent_change,
                          })
                        );
                        break;
                      case "Account Setup or Information":
                        tableData[index].sub = reasonAccountSetupData.map(
                          ({
                            call_subreason,
                            call_volume,
                            percent_change,
                          }: {
                            call_subreason: string;
                            call_volume: string;
                            percent_change: string;
                          }) => ({
                            callsubreason: call_subreason,
                            call_volume: parseInt(call_volume, 10),
                            percent_change,
                          })
                        );
                        break;
                      default:
                        break;
                    }
                  }
                );
                break;
              case "average_wait_time":
                resultArray = JSON.parse(data).map(
                  ({
                    average_waittime,
                    percent_change,
                    call_reason,
                  }: {
                    average_waittime: string;
                    percent_change: string;
                    call_reason: string;
                  }) => [
                    call_reason,
                    percent_change,
                    parseInt(average_waittime, 10),
                  ]
                );

                tableData = resultArray.map(
                  ([call_reason, percent_change, average_waittime]: [
                    string,
                    string,
                    number
                  ]) => {
                    return createData(
                      call_reason,
                      average_waittime,
                      percent_change
                    );
                  }
                );

                resultArray.forEach(
                  (
                    [call_reason, average_waittime, percent_change]: [
                      string,
                      number,
                      string
                    ],
                    index: number
                  ) => {
                    switch (call_reason) {
                      case "Transaction Related":
                        tableData[index].sub = reasonTransactionData.map(
                          ({
                            call_subreason,
                            average_waittime,
                            percent_change,
                          }: {
                            call_subreason: string;
                            average_waittime: string;
                            percent_change: string;
                          }) => ({
                            callsubreason: call_subreason,
                            call_volume: parseInt(average_waittime, 10),
                            percent_change,
                          })
                        );
                        break;
                      case "Online and Mobile Banking":
                        tableData[index].sub = reasonTechnicalData.map(
                          ({
                            call_subreason,
                            average_waittime,
                            percent_change,
                          }: {
                            call_subreason: string;
                            average_waittime: string;
                            percent_change: string;
                          }) => ({
                            callsubreason: call_subreason,
                            call_volume: parseInt(average_waittime, 10),
                            percent_change,
                          })
                        );
                        break;
                      case "Account Management":
                        tableData[index].sub = reasonAccountMgmtData.map(
                          ({
                            call_subreason,
                            average_waittime,
                            percent_change,
                          }: {
                            call_subreason: string;
                            average_waittime: string;
                            percent_change: string;
                          }) => ({
                            callsubreason: call_subreason,
                            call_volume: parseInt(average_waittime, 10),
                            percent_change,
                          })
                        );
                        break;
                      case "Account Setup or Information":
                        tableData[index].sub = reasonAccountSetupData.map(
                          ({
                            call_subreason,
                            average_waittime,
                            percent_change,
                          }: {
                            call_subreason: string;
                            average_waittime: string;
                            percent_change: string;
                          }) => ({
                            callsubreason: call_subreason,
                            call_volume: parseInt(average_waittime, 10),
                            percent_change,
                          })
                        );
                        break;
                      default:
                        break;
                    }
                  }
                );
                break;
              case "average_handle_time":
                resultArray = JSON.parse(data).map(
                  ({
                    average_handletime,
                    percent_change,
                    call_reason,
                  }: {
                    average_handletime: string;
                    percent_change: string;
                    call_reason: string;
                  }) => [
                    call_reason,
                    percent_change,
                    parseInt(average_handletime, 10),
                  ]
                );

                tableData = resultArray.map(
                  ([call_reason, percent_change, average_handletime]: [
                    string,
                    string,
                    number
                  ]) => {
                    return createData(
                      call_reason,
                      average_handletime,
                      percent_change
                    );
                  }
                );

                resultArray.forEach(
                  (
                    [call_reason, average_handletime, percent_change]: [
                      string,
                      number,
                      string
                    ],
                    index: number
                  ) => {
                    switch (call_reason) {
                      case "Transaction Related":
                        tableData[index].sub = reasonTransactionData.map(
                          ({
                            call_subreason,
                            average_handletime,
                            percent_change,
                          }: {
                            call_subreason: string;
                            average_handletime: string;
                            percent_change: string;
                          }) => ({
                            callsubreason: call_subreason,
                            call_volume: parseInt(average_handletime, 10),
                            percent_change,
                          })
                        );
                        break;
                      case "Online and Mobile Banking":
                        tableData[index].sub = reasonTechnicalData.map(
                          ({
                            call_subreason,
                            average_handletime,
                            percent_change,
                          }: {
                            call_subreason: string;
                            average_handletime: string;
                            percent_change: string;
                          }) => ({
                            callsubreason: call_subreason,
                            call_volume: parseInt(average_handletime, 10),
                            percent_change,
                          })
                        );
                        break;
                      case "Account Management":
                        tableData[index].sub = reasonAccountMgmtData.map(
                          ({
                            call_subreason,
                            average_handletime,
                            percent_change,
                          }: {
                            call_subreason: string;
                            average_handletime: string;
                            percent_change: string;
                          }) => ({
                            callsubreason: call_subreason,
                            call_volume: parseInt(average_handletime, 10),
                            percent_change,
                          })
                        );
                        break;
                      case "Account Setup or Information":
                        tableData[index].sub = reasonAccountSetupData.map(
                          ({
                            call_subreason,
                            average_handletime,
                            percent_change,
                          }: {
                            call_subreason: string;
                            average_handletime: string;
                            percent_change: string;
                          }) => ({
                            callsubreason: call_subreason,
                            call_volume: parseInt(average_handletime, 10),
                            percent_change,
                          })
                        );
                        break;
                      default:
                        break;
                    }
                  }
                );
                break;
              case "resolution_rate":
                resultArray = JSON.parse(data).map(
                  ({
                    resolution_rate,
                    percent_change,
                    call_reason,
                  }: {
                    resolution_rate: string;
                    percent_change: string;
                    call_reason: string;
                  }) => [
                    call_reason,
                    percent_change,
                    parseInt(resolution_rate, 10),
                  ]
                );

                tableData = resultArray.map(
                  ([call_reason, percent_change, resolution_rate]: [
                    string,
                    string,
                    number
                  ]) => {
                    return createData(
                      call_reason,
                      resolution_rate,
                      percent_change
                    );
                  }
                );

                resultArray.forEach(
                  (
                    [call_reason, resolution_rate, percent_change]: [
                      string,
                      number,
                      string
                    ],
                    index: number
                  ) => {
                    switch (call_reason) {
                      case "Transaction Related":
                        tableData[index].sub = reasonTransactionData.map(
                          ({
                            call_subreason,
                            resolution_rate,
                            percent_change,
                          }: {
                            call_subreason: string;
                            resolution_rate: string;
                            percent_change: string;
                          }) => ({
                            callsubreason: call_subreason,
                            call_volume: parseInt(resolution_rate, 10),
                            percent_change,
                          })
                        );
                        break;
                      case "Online and Mobile Banking":
                        tableData[index].sub = reasonTechnicalData.map(
                          ({
                            call_subreason,
                            resolution_rate,
                            percent_change,
                          }: {
                            call_subreason: string;
                            resolution_rate: string;
                            percent_change: string;
                          }) => ({
                            callsubreason: call_subreason,
                            call_volume: parseInt(resolution_rate, 10),
                            percent_change,
                          })
                        );
                        break;
                      case "Account Management":
                        tableData[index].sub = reasonAccountMgmtData.map(
                          ({
                            call_subreason,
                            resolution_rate,
                            percent_change,
                          }: {
                            call_subreason: string;
                            resolution_rate: string;
                            percent_change: string;
                          }) => ({
                            callsubreason: call_subreason,
                            call_volume: parseInt(resolution_rate, 10),
                            percent_change,
                          })
                        );
                        break;
                      case "Account Setup or Information":
                        tableData[index].sub = reasonAccountSetupData.map(
                          ({
                            call_subreason,
                            resolution_rate,
                            percent_change,
                          }: {
                            call_subreason: string;
                            resolution_rate: string;
                            percent_change: string;
                          }) => ({
                            callsubreason: call_subreason,
                            call_volume: parseInt(resolution_rate, 10),
                            percent_change,
                          })
                        );
                        break;
                      default:
                        break;
                    }
                  }
                );
                break;
              case "average_sentiment":
                resultArray = JSON.parse(data).map(
                  ({
                    average_sentiment,
                    percent_change,
                    call_reason,
                  }: {
                    average_sentiment: string;
                    percent_change: string;
                    call_reason: string;
                  }) => [
                    call_reason,
                    percent_change,
                    parseInt(average_sentiment, 10),
                  ]
                );

                tableData = resultArray.map(
                  ([call_reason, percent_change, average_sentiment]: [
                    string,
                    string,
                    number
                  ]) => {
                    return createData(
                      call_reason,
                      average_sentiment,
                      percent_change
                    );
                  }
                );

                resultArray.forEach(
                  (
                    [call_reason, average_sentiment, percent_change]: [
                      string,
                      number,
                      string
                    ],
                    index: number
                  ) => {
                    switch (call_reason) {
                      case "Transaction Related":
                        tableData[index].sub = reasonTransactionData.map(
                          ({
                            call_subreason,
                            average_sentiment,
                            percent_change,
                          }: {
                            call_subreason: string;
                            average_sentiment: string;
                            percent_change: string;
                          }) => ({
                            callsubreason: call_subreason,
                            call_volume: parseInt(average_sentiment, 10),
                            percent_change,
                          })
                        );
                        break;
                      case "Online and Mobile Banking":
                        tableData[index].sub = reasonTechnicalData.map(
                          ({
                            call_subreason,
                            average_sentiment,
                            percent_change,
                          }: {
                            call_subreason: string;
                            average_sentiment: string;
                            percent_change: string;
                          }) => ({
                            callsubreason: call_subreason,
                            call_volume: parseInt(average_sentiment, 10),
                            percent_change,
                          })
                        );
                        break;
                      case "Account Management":
                        tableData[index].sub = reasonAccountMgmtData.map(
                          ({
                            call_subreason,
                            average_sentiment,
                            percent_change,
                          }: {
                            call_subreason: string;
                            average_sentiment: string;
                            percent_change: string;
                          }) => ({
                            callsubreason: call_subreason,
                            call_volume: parseInt(average_sentiment, 10),
                            percent_change,
                          })
                        );
                        break;
                      case "Account Setup or Information":
                        tableData[index].sub = reasonAccountSetupData.map(
                          ({
                            call_subreason,
                            average_sentiment,
                            percent_change,
                          }: {
                            call_subreason: string;
                            average_sentiment: string;
                            percent_change: string;
                          }) => ({
                            callsubreason: call_subreason,
                            call_volume: parseInt(average_sentiment, 10),
                            percent_change,
                          })
                        );
                        break;
                      default:
                        break;
                    }
                  }
                );
                break;
              case "unresolved_calls":
                resultArray = JSON.parse(data).map(
                  ({
                    total_unresolved,
                    percent_change,
                    call_reason,
                  }: {
                    total_unresolved: string;
                    percent_change: string;
                    call_reason: string;
                  }) => [
                    call_reason,
                    percent_change,
                    parseInt(total_unresolved, 10),
                  ]
                );

                tableData = resultArray.map(
                  ([call_reason, percent_change, total_unresolved]: [
                    string,
                    string,
                    number
                  ]) => {
                    return createData(
                      call_reason,
                      total_unresolved,
                      percent_change
                    );
                  }
                );

                resultArray.forEach(
                  (
                    [call_reason, total_unresolved, percent_change]: [
                      string,
                      number,
                      string
                    ],
                    index: number
                  ) => {
                    switch (call_reason) {
                      case "Transaction Related":
                        tableData[index].sub = reasonTransactionData.map(
                          ({
                            call_subreason,
                            total_unresolved,
                            percent_change,
                          }: {
                            call_subreason: string;
                            total_unresolved: string;
                            percent_change: string;
                          }) => ({
                            callsubreason: call_subreason,
                            call_volume: parseInt(total_unresolved, 10),
                            percent_change,
                          })
                        );
                        break;
                      case "Online and Mobile Banking":
                        tableData[index].sub = reasonTechnicalData.map(
                          ({
                            call_subreason,
                            total_unresolved,
                            percent_change,
                          }: {
                            call_subreason: string;
                            total_unresolved: string;
                            percent_change: string;
                          }) => ({
                            callsubreason: call_subreason,
                            call_volume: parseInt(total_unresolved, 10),
                            percent_change,
                          })
                        );
                        break;
                      case "Account Management":
                        tableData[index].sub = reasonAccountMgmtData.map(
                          ({
                            call_subreason,
                            total_unresolved,
                            percent_change,
                          }: {
                            call_subreason: string;
                            total_unresolved: string;
                            percent_change: string;
                          }) => ({
                            callsubreason: call_subreason,
                            call_volume: parseInt(total_unresolved, 10),
                            percent_change,
                          })
                        );
                        break;
                      case "Account Setup or Information":
                        tableData[index].sub = reasonAccountSetupData.map(
                          ({
                            call_subreason,
                            total_unresolved,
                            percent_change,
                          }: {
                            call_subreason: string;
                            total_unresolved: string;
                            percent_change: string;
                          }) => ({
                            callsubreason: call_subreason,
                            call_volume: parseInt(total_unresolved, 10),
                            percent_change,
                          })
                        );
                        break;
                      default:
                        break;
                    }
                  }
                );
                break;
              default:
                resultArray = JSON.parse(data).map(
                  ({
                    call_volume,
                    percent_change,
                    call_reason,
                  }: {
                    call_volume: string;
                    percent_change: string;
                    call_reason: string;
                  }) => [call_reason, percent_change, parseInt(call_volume, 10)]
                );

                tableData = resultArray.map(
                  ([call_reason, percent_change, call_volume]: [
                    string,
                    string,
                    number
                  ]) => {
                    return createData(call_reason, call_volume, percent_change);
                  }
                );

                resultArray.forEach(
                  (
                    [call_reason, call_volume, percent_change]: [
                      string,
                      number,
                      string
                    ],
                    index: number
                  ) => {
                    switch (call_reason) {
                      case "Transaction Related":
                        tableData[index].sub = reasonTransactionData.map(
                          ({
                            call_subreason,
                            call_volume,
                            percent_change,
                          }: {
                            call_subreason: string;
                            call_volume: string;
                            percent_change: string;
                          }) => ({
                            callsubreason: call_subreason,
                            call_volume: parseInt(call_volume, 10),
                            percent_change,
                          })
                        );
                        break;
                      case "Online and Mobile Banking":
                        tableData[index].sub = reasonTechnicalData.map(
                          ({
                            call_subreason,
                            call_volume,
                            percent_change,
                          }: {
                            call_subreason: string;
                            call_volume: string;
                            percent_change: string;
                          }) => ({
                            callsubreason: call_subreason,
                            call_volume: parseInt(call_volume, 10),
                            percent_change,
                          })
                        );
                        break;
                      case "Account Management":
                        tableData[index].sub = reasonAccountMgmtData.map(
                          ({
                            call_subreason,
                            call_volume,
                            percent_change,
                          }: {
                            call_subreason: string;
                            call_volume: string;
                            percent_change: string;
                          }) => ({
                            callsubreason: call_subreason,
                            call_volume: parseInt(call_volume, 10),
                            percent_change,
                          })
                        );
                        break;
                      case "Account Setup or Information":
                        tableData[index].sub = reasonAccountSetupData.map(
                          ({
                            call_subreason,
                            call_volume,
                            percent_change,
                          }: {
                            call_subreason: string;
                            call_volume: string;
                            percent_change: string;
                          }) => ({
                            callsubreason: call_subreason,
                            call_volume: parseInt(call_volume, 10),
                            percent_change,
                          })
                        );
                        break;
                      default:
                        break;
                    }
                  }
                );
            }

            setTableData(tableData);
            setIsLoading(false);
          })
          .catch((error) => {
            console.error("Error fetching data:", error);
          });
      })
      .catch((error) => {
        console.error("Error fetching or processing data:", error);
      });
  }, [startDate, endDate, selectedCallCenter, selectedRadio]);

  function createData(
    call_reason: string,
    call_volume: number,
    percent_change: string
  ) {
    return {
      call_reason,
      call_volume,
      percent_change,
      sub: [
        {
          callsubreason: "call sub reason 1",
          call_volume: 23,
          percent_change: 3,
        },
        {
          callsubreason: "call sub reason 2",
          call_volume: 43,
          percent_change: 1,
        },
      ],
    };
  }

  function Row(props: any) {
    const { row } = props;

    return (
      <React.Fragment>
        <TableRow sx={{ "& > *": { borderBottom: "unset" } }}>
          <TableCell style={{ width: "5%" }}>
            <IconButton
              aria-label='expand row'
              size='small'
              onClick={() =>
                setOpenRow(openRow === row.call_reason ? null : row.call_reason)
              }
            >
              {openRow === row.call_reason ? (
                <KeyboardArrowUpIcon />
              ) : (
                <KeyboardArrowDownIcon />
              )}
            </IconButton>
          </TableCell>
          <TableCell style={{ width: "35%" }} component='th' scope='row'>
            <ListItemText
              className={
                selectedReason === row.call_reason
                  ? "selected"
                  : selectedReason === ""
                  ? "default"
                  : "not-selected"
              }
              style={{
                cursor: "pointer",
                color: selectedReason === row.call_reason ? "#1976d2" : "black",
              }}
              data-tooltip-id='ops-call-reasons-tooltip'
              onClick={() => handleReasonSelection(row.call_reason)}
            >
              {row.call_reason}
            </ListItemText>
          </TableCell>
          <TableCell style={{ width: "30%" }} align='right'>
            {selectedRadio === "average_handle_time" && (
              <span>{row.call_volume}min</span>
            )}
            {selectedRadio === "average_wait_time" && (
              <span>{row.call_volume}sec</span>
            )}
            {selectedRadio === "resolution_rate" && (
              <span>{row.call_volume}%</span>
            )}
            {selectedRadio !== "average_handle_time" &&
              selectedRadio !== "average_wait_time" &&
              selectedRadio !== "resolution_rate" && (
                <span>{row.call_volume}</span>
              )}
          </TableCell>
          <TableCell
            className='percent-change'
            style={{ width: "30%" }}
            align='right'
          >
            {selectedOption !== "Last 7 days" ||
            row.percent_change === "N/A" ? (
              <span className='bold-num no-arrow'>N/A</span>
            ) : (
              <span
                className={`${
                  parseFloat(row.percent_change) === 0
                    ? "no-arrow"
                    : (selectedRadio === "total_call_volume" &&
                        (parseFloat(row.percent_change) > 0
                          ? "red-arrow"
                          : "green-arrow")) ||
                      (selectedRadio === "resolution_rate" &&
                        (parseFloat(row.percent_change) < 0
                          ? "red-arrow"
                          : "green-arrow")) ||
                      (selectedRadio === "average_wait_time" &&
                        (parseFloat(row.percent_change) > 0
                          ? "red-arrow"
                          : "green-arrow")) ||
                      (selectedRadio === "average_handle_time" &&
                        (parseFloat(row.percent_change) > 0
                          ? "red-arrow"
                          : "green-arrow")) ||
                      (selectedRadio === "average_sentiment" &&
                        (parseFloat(row.percent_change) < 0
                          ? "red-arrow"
                          : "green-arrow")) ||
                      (selectedRadio === "unresolved_calls" &&
                        (parseFloat(row.percent_change) > 0
                          ? "red-arrow"
                          : "green-arrow"))
                } number`}
              >
                {parseFloat(row.percent_change) === 0
                  ? "00.0%"
                  : (parseFloat(row.percent_change) < 0 ? "" : "") +
                    Math.abs(parseFloat(row.percent_change))
                      .toFixed(1)
                      .padStart(4, "0") +
                    "%"}
                {parseFloat(row.percent_change) === 0
                  ? "⬅"
                  : parseFloat(row.percent_change) < 0
                  ? "⬇"
                  : "⬆"}
              </span>
            )}
          </TableCell>
        </TableRow>

        <TableRow>
          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
            <Collapse
              in={openRow === row.call_reason}
              timeout='auto'
              unmountOnExit
            >
              <Table aria-label='purchases'>
                <TableHead style={{ display: "none" }}>
                  <TableRow>
                    <TableCell style={{ width: "5%" }} />
                    <TableCell style={{ width: "35%" }}>Sub Reasons</TableCell>
                    <TableCell style={{ width: "30%" }}>Value</TableCell>
                    <TableCell style={{ width: "30%" }} align='right'>
                      % Change
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {row.sub.map((subRow: any) => (
                    <TableRow key={subRow.callsubreason}>
                      <TableCell style={{ width: "5%" }} />
                      <TableCell
                        style={{ width: "35%" }}
                        component='th'
                        scope='row'
                      >
                        <ListItemText
                          className={
                            selectedSubReason === subRow.callsubreason
                              ? "selected"
                              : selectedSubReason === ""
                              ? "default"
                              : "not-selected"
                          }
                          style={{
                            cursor: "pointer",
                            color:
                              selectedSubReason === subRow.callsubreason
                                ? "#1976d2"
                                : "black",
                          }}
                          onClick={() =>
                            handleSubReasonSelection(subRow.callsubreason)
                          }
                        >
                          {subRow.callsubreason}
                        </ListItemText>
                      </TableCell>
                      <TableCell style={{ width: "30%" }} align='right'>
                        {selectedRadio === "average_handle_time" && (
                          <span>{subRow.call_volume}min</span>
                        )}
                        {selectedRadio === "average_wait_time" && (
                          <span>{subRow.call_volume}sec</span>
                        )}
                        {selectedRadio === "resolution_rate" && (
                          <span>{subRow.call_volume}%</span>
                        )}
                        {selectedRadio !== "average_handle_time" &&
                          selectedRadio !== "average_wait_time" &&
                          selectedRadio !== "resolution_rate" && (
                            <span>{subRow.call_volume}</span>
                          )}
                      </TableCell>
                      <TableCell
                        className='percent-change'
                        style={{ width: "30%" }}
                        align='right'
                      >
                        {selectedOption !== "Last 7 days" ||
                        row.percent_change === "N/A" ? (
                          <span className='bold-num no-arrow'>N/A</span>
                        ) : (
                          <span
                            className={`${
                              parseFloat(subRow.percent_change) === 0
                                ? "no-arrow"
                                : (selectedRadio === "total_call_volume" &&
                                    (parseFloat(subRow.percent_change) > 0
                                      ? "red-arrow"
                                      : "green-arrow")) ||
                                  (selectedRadio === "resolution_rate" &&
                                    (parseFloat(subRow.percent_change) < 0
                                      ? "red-arrow"
                                      : "green-arrow")) ||
                                  (selectedRadio === "average_wait_time" &&
                                    (parseFloat(subRow.percent_change) > 0
                                      ? "red-arrow"
                                      : "green-arrow")) ||
                                  (selectedRadio === "average_handle_time" &&
                                    (parseFloat(subRow.percent_change) > 0
                                      ? "red-arrow"
                                      : "green-arrow")) ||
                                  (selectedRadio === "average_sentiment" &&
                                    (parseFloat(subRow.percent_change) < 0
                                      ? "red-arrow"
                                      : "green-arrow")) ||
                                  (selectedRadio === "unresolved_calls" &&
                                    (parseFloat(subRow.percent_change) > 0
                                      ? "red-arrow"
                                      : "green-arrow"))
                            } number`}
                          >
                            {parseFloat(subRow.percent_change) === 0
                              ? "00.0%"
                              : (parseFloat(subRow.percent_change) < 0
                                  ? ""
                                  : "") +
                                Math.abs(parseFloat(subRow.percent_change))
                                  .toFixed(1)
                                  .padStart(4, "0") +
                                "%"}
                            {parseFloat(subRow.percent_change) === 0
                              ? "⬅"
                              : parseFloat(subRow.percent_change) < 0
                              ? "⬇"
                              : "⬆"}
                          </span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Collapse>
          </TableCell>
        </TableRow>
      </React.Fragment>
    );
  }

  return (
    <TableContainer className='ops-table-container' component={Paper}>
      <h3 style={{ paddingLeft: "15px" }}>
        {displayText} Across Call Reasons
        {(displayText === "Resolution Rate" ||
          displayText === "Average Sentiment" ||
          displayText === "Unresolved Calls") &&
          (mode === "dark" ? (
            <span className='shine'>
              <AutoAwesomeIcon />
            </span>
          ) : (
            <span className='ai-driven'>
              <AutoAwesomeIcon />
            </span>
          ))}
      </h3>
      {!isLoading ? (
        <Table className='collapsible-table' aria-label='collapsible table'>
          <TableHead>
            <TableRow>
              <TableCell style={{ width: "5%" }} />
              <TableCell style={{ width: "35%" }}>Call Reasons</TableCell>
              <TableCell style={{ width: "30%" }} align='right'>
                Value
              </TableCell>
              <TableCell style={{ width: "30%" }} align='right'>
                % Change
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Object.keys(tableData).length > 0 &&
              tableData.map((row: any) => (
                <Row key={row.call_reason} row={row} />
              ))}
          </TableBody>
        </Table>
      ) : (
        <Skeleton height={"100%"} width={"100%"} />
      )}
      <ReactTooltip
        id='ops-call-reasons-tooltip'
        style={{
          borderRadius: "5px",
          ...(mode === "dark" && {
            backgroundColor: "#f5f5f5",
            color: "black",
            
          }),
        }}
        place='bottom'
        content='Select to visualize'
      />
    </TableContainer>
  );
}

export default CollapsibleTable;
